import { map } from 'ramda';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { MediaManagerSDKFile } from '../../../typings/media-manager-sdk';
import { CreateVideoClickTarget } from '../../constants/Bi';
import { PageTabsNames } from '../../constants/PageTabsNames';
import { useTrackCreateVideoClick } from '../../utils/hooks/bi/useTrackCreateVideoClick';
import {
  NotificationButtons,
  NotificationMessages,
  useTrackNotificationInteraction,
} from '../../utils/hooks/bi/useTrackNotificationInteraction';
import { useFilesAction, useGetFilesData } from '../../utils/hooks/useFiles';
import { FilesStateActionType } from '../../utils/hooks/useFiles/useFiles.types';
import { useModal } from '../../utils/hooks/useModal';
import {
  ModalName,
  ModalTheme,
} from '../../utils/hooks/useModal/useModal.types';
import { useDeleteAction } from '../../utils/hooks/useMyVideoItemActions';
import { useWixmpGetFilesInfo } from '../../utils/hooks/usePrivateWixmp/usePrivateWixmp';
import { useVideoMaker } from '../../utils/hooks/useVideoMaker';
import { useVideoMakerFileSaved } from '../../utils/hooks/useVideoMaker/useVideoMaker';
import { AddItem } from '../common/wixCommon';
import { EmptyStateNoVideoFiles } from '../EmptyStates';
import { GalleryItem } from '../GalleryItem';
import styles from '../HomePage/HomePage.scss';
import { IOuter } from './MyVideosContent.types';

export const MyVideosContent: FC<IOuter> = ({ mediaToken }) => {
  const { openVideoMaker } = useVideoMaker();
  const { files } = useGetFilesData();
  const { onFileAction } = useFilesAction();
  const { deleteFileAction } = useDeleteAction({
    mediaToken,
  });

  const {
    trackErrorClick,
    trackErrorShown,
  } = useTrackNotificationInteraction();

  const { modalComponent: DeleteVideoFileModal, onOpenModal } = useModal(
    ModalName.DeleteVideoFile,
    {
      title: <FormattedMessage id="video-maker-home.modal.delete-item.label" />,
      confirmText: (
        <FormattedMessage id="video-maker-home.modal.delete-item.confirm" />
      ),
      cancelText: (
        <FormattedMessage id="video-maker-home.modal.delete-item.cancel" />
      ),
      message: (
        <FormattedMessage id="video-maker-home.modal.delete-item.message" />
      ),
      theme: ModalTheme.Red,
      onOk: ({ file }) => {
        if (!file) {
          return;
        }

        onFileAction({
          type: FilesStateActionType.SetActive,
          payload: { files: [file] },
        });

        trackErrorClick({
          errorMessage: NotificationMessages.DeleteModal,
          fileId: file.id,
          buttonName: NotificationButtons.Ok,
        });

        deleteFileAction(file);
      },
      onCancel: ({ file }) => {
        if (!file) {
          return;
        }
        trackErrorClick({
          errorMessage: NotificationMessages.DeleteModal,
          fileId: file.id,
          buttonName: NotificationButtons.Cancel,
        });
      },
      onClose: ({ file }) => {
        if (!file) {
          return;
        }
        trackErrorClick({
          errorMessage: NotificationMessages.DeleteModal,
          fileId: file.id,
          buttonName: NotificationButtons.Close,
        });
      },
    },
  );

  const handleOpenDeleteFileModal = useCallback(
    (file: MediaManagerSDKFile) => {
      onOpenModal({ file });
      trackErrorShown({
        errorMessage: NotificationMessages.DeleteModal,
        fileId: file.id,
      });
    },
    [onOpenModal, trackErrorShown],
  );

  const { trackVideoMakerCreateVideoClick } = useTrackCreateVideoClick();

  const handleCreateNewVideo = useCallback(() => {
    openVideoMaker();

    trackVideoMakerCreateVideoClick({
      target: CreateVideoClickTarget.CreateNewVideoGallery,
      tabName: PageTabsNames.MyVideos,
    });
  }, [openVideoMaker, trackVideoMakerCreateVideoClick]);

  const { initRequest: getSavedFileInfo } = useWixmpGetFilesInfo({
    mediaToken,
  });

  useVideoMakerFileSaved(({ fileId }) => {
    getSavedFileInfo(fileId)
      .then(({ data }) => {
        onFileAction({
          type: FilesStateActionType.Prepend,
          payload: { files: data },
        });
      })
      .catch((error) => {
        throw error;
      });
  });

  return (
    <>
      {DeleteVideoFileModal}

      {files.length ? (
        <div
          className={styles.listItemsHolder}
          data-hook="my-videos-items-content"
        >
          {map(
            (file) => (
              <GalleryItem
                key={file.id}
                file={file}
                mediaToken={mediaToken}
                onOpenDeleteFileModal={handleOpenDeleteFileModal}
              />
            ),
            files,
          )}

          <div className={styles.addItemHolder}>
            <AddItem
              size="medium"
              theme="image"
              onClick={handleCreateNewVideo}
              dataHook="create-new-video-gallery-button"
            />
          </div>
        </div>
      ) : (
        <EmptyStateNoVideoFiles />
      )}
    </>
  );
};
